import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';
import { defaultDateTimeFormatMap as DF } from '@rover/utilities/datetime';
import { WEEKDAY_ENGLISH_PARAMS } from '@rover/utilities/weekday';

import {
  LOOSE_DATES_PARAM,
  parseLooseDates,
  serializeLooseDates,
  sortDates,
} from '../utils/looseDates';
import {
  FREQUENCY_PARAM,
  isRecurringFrequency,
  parseWeekDays,
  setWeekDayParams,
} from '../utils/recurringDates';

export const DATE_SELECTION_PARAMS = [
  'start_date',
  'end_date',
  LOOSE_DATES_PARAM,
  FREQUENCY_PARAM,
  ...WEEKDAY_ENGLISH_PARAMS.map(({ paramName }) => paramName),
];

export type SelectedDates = {
  startDate?: Date;
  endDate?: Date;
  dates?: Date[];
  isRecurring?: boolean;
  weekDays?: number[];
};

/**
 * The profile reads its dates from `start_date`/`end_date`, so writing them back to
 * the query string is what keeps rates, availability and the contact link in sync.
 * A non-contiguous selection also writes every day to `dates`, and publishes its
 * first and last day as the range so rates and availability keep working. A repeating
 * selection has no range of its own: it writes the `frequency` and one parameter per
 * weekday next to the day it starts from, which is how the contact page reads it.
 */
export const useUpdateSelectedDates = (): ((dates: SelectedDates) => void) => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    ({ startDate, endDate, dates, isRecurring, weekDays }: SelectedDates) => {
      const params = new URLSearchParams(location.search);

      if (isRecurring) {
        if (startDate) params.set('start_date', moment(startDate).format(DF.DATE_ISO));
        else params.delete('start_date');
        params.delete('end_date');
        params.delete(LOOSE_DATES_PARAM);
        params.set(FREQUENCY_PARAM, ServiceFrequencyEnum.REPEAT);
        setWeekDayParams(params, weekDays ?? []);
        navigate({ search: params.toString() }, { replace: true });
        return;
      }

      params.delete(FREQUENCY_PARAM);
      setWeekDayParams(params, []);

      const looseDates = sortDates(dates ?? []);

      const rangeStart = looseDates[0] ?? startDate;
      const rangeEnd = looseDates.length > 0 ? looseDates[looseDates.length - 1] : endDate;

      if (rangeStart) params.set('start_date', moment(rangeStart).format(DF.DATE_ISO));
      else params.delete('start_date');

      if (rangeEnd) params.set('end_date', moment(rangeEnd).format(DF.DATE_ISO));
      else params.delete('end_date');

      if (looseDates.length > 0) params.set(LOOSE_DATES_PARAM, serializeLooseDates(looseDates));
      else params.delete(LOOSE_DATES_PARAM);

      navigate({ search: params.toString() }, { replace: true });
    },
    [location.search, navigate]
  );
};

// The days of a non-contiguous selection currently applied to the page.
export const useSelectedLooseDates = (): Date[] => {
  const location = useLocation();

  return useMemo(
    () => parseLooseDates(new URLSearchParams(location.search).get(LOOSE_DATES_PARAM)),
    [location.search]
  );
};

export type SelectedRecurrence = {
  isRecurring: boolean;
  weekDays: number[];
};

// The weekly repetition currently applied to the page.
export const useSelectedRecurrence = (): SelectedRecurrence => {
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      isRecurring: isRecurringFrequency(params.get(FREQUENCY_PARAM)),
      weekDays: parseWeekDays(params),
    };
  }, [location.search]);
};

export default useUpdateSelectedDates;
